(() => {
  const COUNTRY_ALIASES = Object.freeze({
    uk: 'GB',
    britain: 'GB',
    england: 'GB',
    'great britain': 'GB',
    'united kingdom': 'GB',
    usa: 'US',
    'united states': 'US',
    'united states of america': 'US',
    uae: 'AE',
    'united arab emirates': 'AE',
    colombia: 'CO',
    colombie: 'CO',
    morocco: 'MA',
    maroc: 'MA',
    marocco: 'MA',
    marokko: 'MA',
    marruecos: 'MA',
    marrocos: 'MA',
    'royaume du maroc': 'MA',
    'south korea': 'KR',
    'republic of korea': 'KR',
    'north korea': 'KP',
    russia: 'RU',
    vietnam: 'VN',
    'viet nam': 'VN',
    czechia: 'CZ',
    'czech republic': 'CZ',
    'ivory coast': 'CI',
    'cote d ivoire': 'CI',
    swaziland: 'SZ',
    eswatini: 'SZ'
  });

  let lastLightboxTrigger = null;

  function normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function parseList(value) {
    return String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function buildCountryLookup(coordinates) {
    const lookup = new Map();

    Object.entries(coordinates || {}).forEach(([key, country]) => {
      const code = country.iso2 || key;
      lookup.set(normalize(key), code);
      if (country.iso2) lookup.set(normalize(country.iso2), code);
      if (country.iso3) lookup.set(normalize(country.iso3), code);
      if (country.name) lookup.set(normalize(country.name), code);
    });

    Object.entries(COUNTRY_ALIASES).forEach(([name, code]) => {
      lookup.set(normalize(name), code);
    });

    return lookup;
  }

  function resolveCountryCode(item, coordinates, lookup) {
    const candidates = [
      item?.dataset?.countryCode,
      item?.dataset?.countryName,
      item?.dataset?.countryKey
    ];

    for (const candidate of candidates) {
      const direct = String(candidate || '').trim().toUpperCase();
      if (direct && coordinates?.[direct]) {
        return coordinates[direct].iso2 || direct;
      }

      const matched = lookup.get(normalize(candidate));
      if (matched) return matched;
    }

    if (/^[A-Z]{2}$/.test(String(item?.dataset?.countryCode || '').trim().toUpperCase())) {
      return String(item.dataset.countryCode).trim().toUpperCase();
    }

    return String(item?.dataset?.countryKey || '').trim();
  }

  function sortSelectOptions(select) {
    if (!select) return;
    const first = select.options[0];
    const options = Array.from(select.options).slice(1);

    options.sort((a, b) => {
      const countDifference = (Number.parseInt(b.dataset.count || '0', 10) || 0)
        - (Number.parseInt(a.dataset.count || '0', 10) || 0);
      if (countDifference) return countDifference;
      return a.textContent.localeCompare(b.textContent, undefined, { sensitivity: 'base' });
    });

    select.replaceChildren(first, ...options);
  }

  function initCarousel(carousel) {
    const section = carousel.closest('section');
    const previous = section?.querySelector('[data-bvm-carousel-prev]');
    const next = section?.querySelector('[data-bvm-carousel-next]');
    if (!previous || !next) return;

    function distance() {
      const item = carousel.firstElementChild;
      return item ? item.getBoundingClientRect().width + 18 : carousel.clientWidth * 0.8;
    }

    function update() {
      const maximum = carousel.scrollWidth - carousel.clientWidth - 2;
      previous.disabled = carousel.scrollLeft <= 2;
      next.disabled = carousel.scrollLeft >= maximum;
    }

    previous.addEventListener('click', () => {
      carousel.scrollBy({ left: -distance(), behavior: 'smooth' });
    });
    next.addEventListener('click', () => {
      carousel.scrollBy({ left: distance(), behavior: 'smooth' });
    });
    carousel.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  function sortCustomerCards(cards, grid) {
    cards.sort((first, second) => {
      const firstHasLink = Boolean(first.dataset.buildUrl);
      const secondHasLink = Boolean(second.dataset.buildUrl);
      const linkDifference = Number(secondHasLink) - Number(firstHasLink);
      if (linkDifference) return linkDifference;

      const firstHasName = Boolean(first.dataset.customerName);
      const secondHasName = Boolean(second.dataset.customerName);
      const nameDifference = Number(secondHasName) - Number(firstHasName);
      if (nameDifference) return nameDifference;

      const firstIndex = Number.parseInt(first.dataset.bvmCardIndex || '0', 10);
      const secondIndex = Number.parseInt(second.dataset.bvmCardIndex || '0', 10);
      return firstIndex - secondIndex;
    });

    cards.forEach((card) => grid?.appendChild(card));
  }

  function parseJsonArray(root, selector) {
    const node = root.querySelector(selector);
    if (!node) return [];

    try {
      let parsed = JSON.parse(node.textContent || '[]');
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      if (!Array.isArray(parsed)) return [];

      return [...new Set(
        parsed
          .map((item) => String(item || '').trim().toUpperCase())
          .filter((item) => /^[A-Z]{2,3}$/.test(item))
      )];
    } catch {
      return [];
    }
  }

  function parseBuildCountryRecords(root, selector = '[data-bvm-build-countries]') {
    const node = root.querySelector(selector);
    if (!node) return [];

    try {
      let parsed = JSON.parse(node.textContent || '[]');
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((record) => ({
          key: String(record?.key || '').trim(),
          name: String(record?.name || '').trim(),
          code: String(record?.code || '').trim().toUpperCase(),
          count: Math.max(0, Number.parseInt(record?.count || '0', 10) || 0)
        }))
        .filter((record) => record.key || record.name || record.code);
    } catch {
      return [];
    }
  }

  function calculateMarkerSizes(count, minimumCount, maximumCount) {
    const safeCount = Math.max(1, Number(count) || 1);
    const safeMinimum = Math.max(1, Number(minimumCount) || 1);
    const safeMaximum = Math.max(safeMinimum, Number(maximumCount) || safeMinimum);
    const minimumDesktop = 6;
    const maximumDesktop = 17;
    const minimumMobile = 5;
    const maximumMobile = 15;

    let ratio = 0;
    if (safeMaximum > safeMinimum) {
      ratio = (Math.sqrt(safeCount) - Math.sqrt(safeMinimum))
        / (Math.sqrt(safeMaximum) - Math.sqrt(safeMinimum));
    }
    ratio = Math.max(0, Math.min(1, ratio));

    const desktop = minimumDesktop + ((maximumDesktop - minimumDesktop) * ratio);
    const mobile = minimumMobile + ((maximumMobile - minimumMobile) * ratio);

    return {
      desktop: Number(desktop.toFixed(2)),
      mobile: Number(mobile.toFixed(2))
    };
  }

  function interpolate(template, values = {}) {
    return Object.entries(values).reduce(
      (copy, [key, value]) => copy.replaceAll(`__${key.toUpperCase()}__`, String(value)),
      String(template || '')
    );
  }

  function getGalleryStrings(root) {
    return {
      allFilters: root.dataset.bvmI18nAllFilters || 'All countries · All parts',
      buildOne: root.dataset.bvmI18nBuildOne || '1 build',
      buildOther: root.dataset.bvmI18nBuildOther || '__COUNT__ builds',
      viewMoreOne: root.dataset.bvmI18nViewMoreOne || 'View one more customer build',
      viewMoreOther: root.dataset.bvmI18nViewMoreOther || 'View __COUNT__ more customer builds',
      emptyCountryProduct: root.dataset.bvmI18nEmptyCountryProduct
        || 'No customer builds from __COUNTRY__ use __PRODUCT__ yet.',
      emptyCountry: root.dataset.bvmI18nEmptyCountry
        || 'No customer builds from __COUNTRY__ are currently published.',
      emptyProduct: root.dataset.bvmI18nEmptyProduct
        || 'No customer builds match the selected Bonvent part.',
      mapHint: root.dataset.bvmI18nMapHint || 'Select a brown marker to filter the gallery.',
      mapView: root.dataset.bvmI18nMapView || 'View builds'
    };
  }

  function formatBuildCount(strings, count) {
    return count === 1
      ? strings.buildOne
      : interpolate(strings.buildOther, { count });
  }

  function findMarkerPosition(desiredX, desiredY, placed, width, height, markerSize) {
    const padding = markerSize / 2 + 3;
    const minimumDistance = markerSize + 4;
    const isFree = (x, y) => placed.every((item) => {
      const dx = item.x - x;
      const dy = item.y - y;
      return Math.sqrt((dx * dx) + (dy * dy)) >= minimumDistance;
    });
    const clampX = (value) => Math.min(width - padding, Math.max(padding, value));
    const clampY = (value) => Math.min(height - padding, Math.max(padding, value));
    const initial = { x: clampX(desiredX), y: clampY(desiredY) };

    if (isFree(initial.x, initial.y)) return initial;

    for (let radius = minimumDistance; radius <= minimumDistance * 4; radius += minimumDistance * 0.72) {
      for (let angle = 0; angle < 360; angle += 30) {
        const radians = (angle * Math.PI) / 180;
        const x = clampX(desiredX + (Math.cos(radians) * radius));
        const y = clampY(desiredY + (Math.sin(radians) * radius));
        if (isFree(x, y)) return { x, y };
      }
    }

    return initial;
  }

  async function inlineMap(root, countries, shippedCountryCodes, onCountrySelect, onViewBuilds, strings) {
    const stage = root.querySelector('[data-bvm-map-stage]');
    const markerLayer = root.querySelector('[data-bvm-map-markers]');
    const shippingMarkerLayer = root.querySelector('[data-bvm-map-shipping-markers]');
    const tooltip = root.querySelector('[data-bvm-map-tooltip]');
    const tooltipCountry = root.querySelector('[data-bvm-map-tooltip-country]');
    const tooltipCount = root.querySelector('[data-bvm-map-tooltip-count]');
    const tooltipView = root.querySelector('[data-bvm-map-tooltip-view]');
    const mapHint = root.querySelector('[data-bvm-map-hint]');

    if (!stage || !markerLayer) return null;

    const shipped = new Set(
      shippedCountryCodes.map((code) => String(code || '').trim().toUpperCase())
    );
    const uniqueCountries = [...new Map(
      [...countries.values()]
        .filter((country) => country?.iso2)
        .map((country) => [country.iso2, country])
    ).values()];

    // A published customer build is also direct proof that Bonvent shipped to that country.
    // Merge represented build countries into the shipping layer so Morocco and any future
    // customer country cannot remain dark because of a delayed sales-reach sync.
    uniqueCountries.forEach((country) => {
      if (country.iso2) shipped.add(String(country.iso2).trim().toUpperCase());
      if (country.iso3) shipped.add(String(country.iso3).trim().toUpperCase());
    });

    let inlineSvg = null;
    const mapUrl = stage.dataset.bvmMapUrl;

    if (mapUrl) {
      try {
        const response = await fetch(mapUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Map HTTP ${response.status}`);

        const source = await response.text();
        const documentSvg = new DOMParser().parseFromString(source, 'image/svg+xml');
        const svg = documentSvg.documentElement;

        if (!svg || svg.nodeName.toLowerCase() !== 'svg') {
          throw new Error('Invalid SVG map');
        }

        svg.classList.add('bvm-cbg__inline-map');
        svg.removeAttribute('width');
        svg.removeAttribute('height');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');

        const importedSvg = document.importNode(svg, true);
        const fallback = stage.querySelector('[data-bvm-map-fallback]');
        if (fallback) fallback.replaceWith(importedSvg);
        else stage.prepend(importedSvg);

        inlineSvg = stage.querySelector('svg');
        const paths = Array.from(inlineSvg.querySelectorAll('[data-iso2], [data-iso3]'));
        const polygonIso2Codes = new Set();
        const coordinates = window.BVM_COUNTRY_COORDINATES || {};
        const shippedIso2Codes = new Set();

        shipped.forEach((rawCode) => {
          const code = String(rawCode || '').trim().toUpperCase();
          const coordinate = coordinates[code];
          const iso2 = String(coordinate?.iso2 || code).trim().toUpperCase();
          if (/^[A-Z]{2}$/.test(iso2)) shippedIso2Codes.add(iso2);
        });

        paths.forEach((path) => {
          const iso2 = String(path.getAttribute('data-iso2') || '').trim().toUpperCase();
          const iso3 = String(path.getAttribute('data-iso3') || '').trim().toUpperCase();
          if (iso2) polygonIso2Codes.add(iso2);
          const isShipped = shippedIso2Codes.has(iso2) || shipped.has(iso2) || shipped.has(iso3);
          path.classList.toggle('is-shipped', isShipped);
          if (isShipped) {
            path.setAttribute('data-bvm-shipped', 'true');
            // Inline priority guarantees the Shopify theme cannot override a verified country.
            path.style.setProperty('fill', 'var(--bvm-map-shipped)', 'important');
            path.style.setProperty('stroke', 'var(--bvm-map-shipped-border)', 'important');
          } else {
            path.removeAttribute('data-bvm-shipped');
            path.style.removeProperty('fill');
            path.style.removeProperty('stroke');
          }
        });

        // Small islands and territories do not all have a visible polygon in the compact SVG.
        // Represent those verified Shopify order destinations with a discreet taupe diamond.
        if (shippingMarkerLayer) {
          shippingMarkerLayer.replaceChildren();
          shippedIso2Codes.forEach((iso2) => {
            if (polygonIso2Codes.has(iso2)) return;
            const coordinate = coordinates[iso2];
            if (!coordinate || !Number.isFinite(coordinate.xPercent) || !Number.isFinite(coordinate.yPercent)) return;

            const marker = document.createElement('span');
            marker.className = 'bvm-cbg__map-shipping-marker';
            marker.dataset.countryCode = iso2;
            marker.style.left = `${coordinate.xPercent}%`;
            marker.style.top = `${coordinate.yPercent}%`;
            marker.title = coordinate.name || iso2;
            shippingMarkerLayer.appendChild(marker);
          });
        }
      } catch (error) {
        stage.dataset.bvmMapError = 'true';
      }
    }

    const markerCountries = uniqueCountries
      .filter((country) => Number.isFinite(country.xPercent) && Number.isFinite(country.yPercent));
    const markerCounts = markerCountries.map((country) => Math.max(1, country.count));
    const minimumMarkerCount = markerCounts.length ? Math.min(...markerCounts) : 1;
    const maximumMarkerCount = markerCounts.length ? Math.max(...markerCounts) : 1;

    const markerRecords = markerCountries
      .sort((first, second) => second.count - first.count)
      .map((country) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bvm-cbg__map-marker';
        button.dataset.bvmMapCountry = country.key;
        button.textContent = '';
        button.dataset.bvmMapCount = String(country.count);
        const markerSizes = calculateMarkerSizes(
          country.count,
          minimumMarkerCount,
          maximumMarkerCount
        );
        button.style.setProperty('--bvm-dot-size', `${markerSizes.desktop}px`);
        button.style.setProperty('--bvm-dot-size-mobile', `${markerSizes.mobile}px`);
        button.style.setProperty('--bvm-hit-size', `${Math.max(18, markerSizes.desktop + 10)}px`);
        button.style.setProperty('--bvm-hit-size-mobile', `${Math.max(16, markerSizes.mobile + 9)}px`);
        button.setAttribute(
          'aria-label',
          `${country.name}: ${formatBuildCount(strings, country.count)}`
        );
        button.setAttribute('aria-pressed', 'false');
        markerLayer.appendChild(button);

        return {
          country,
          button,
          desiredXPercent: country.xPercent,
          desiredYPercent: country.yPercent,
          x: 0,
          y: 0
        };
      });

    let activeKey = '';
    let hoveredRecord = null;

    function updateHint(record = null) {
      if (!mapHint) return;
      mapHint.textContent = record
        ? `${record.country.name} · ${formatBuildCount(strings, record.country.count)}`
        : strings.mapHint;
    }

    function positionTooltip(record) {
      if (!tooltip || !record || tooltip.hidden) return;

      const stageWidth = stage.clientWidth;
      const stageHeight = stage.clientHeight;
      const tooltipWidth = tooltip.offsetWidth || 180;
      const tooltipHeight = tooltip.offsetHeight || 72;
      const margin = 8;
      const left = Math.min(
        stageWidth - (tooltipWidth / 2) - margin,
        Math.max((tooltipWidth / 2) + margin, record.x)
      );
      const shouldShowBelow = record.y - tooltipHeight - 22 < margin;
      const top = Math.min(stageHeight - margin, Math.max(margin, record.y));

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
      tooltip.classList.toggle('is-below', shouldShowBelow);
    }

    function showTooltip(record) {
      if (!tooltip || !tooltipCountry || !tooltipCount || !record) return;
      tooltipCountry.textContent = record.country.name;
      tooltipCount.textContent = formatBuildCount(strings, record.country.count);
      tooltip.hidden = false;
      positionTooltip(record);
      updateHint(record);
    }

    function hideTooltip() {
      if (tooltip) tooltip.hidden = true;
      updateHint();
    }

    function selectedRecord() {
      return markerRecords.find((record) => record.country.key === activeKey) || null;
    }

    function restoreSelectedTooltip() {
      const selected = selectedRecord();
      if (selected) showTooltip(selected);
      else hideTooltip();
    }

    function layoutMarkers() {
      const width = stage.clientWidth;
      const height = stage.clientHeight;
      if (!width || !height) return;

      markerRecords.forEach((record) => {
        // Keep every dot on the true country coordinate. The former collision-avoidance
        // routine could move European markers several countries away (for example France).
        record.x = (record.desiredXPercent / 100) * width;
        record.y = (record.desiredYPercent / 100) * height;
        record.button.style.left = `${record.desiredXPercent}%`;
        record.button.style.top = `${record.desiredYPercent}%`;
      });

      const tooltipRecord = hoveredRecord || selectedRecord();
      if (tooltipRecord) positionTooltip(tooltipRecord);
    }

    markerRecords.forEach((record) => {
      record.button.addEventListener('pointerenter', () => {
        hoveredRecord = record;
        showTooltip(record);
      });
      record.button.addEventListener('pointerleave', () => {
        hoveredRecord = null;
        restoreSelectedTooltip();
      });
      record.button.addEventListener('focus', () => {
        hoveredRecord = record;
        showTooltip(record);
      });
      record.button.addEventListener('blur', () => {
        hoveredRecord = null;
        window.setTimeout(restoreSelectedTooltip, 0);
      });
      record.button.addEventListener('click', () => {
        onCountrySelect(record.country.key, record.country.name, record.country.count);
      });
    });

    tooltipView?.addEventListener('click', () => onViewBuilds());

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(layoutMarkers)
      : null;
    resizeObserver?.observe(stage);
    window.addEventListener('resize', layoutMarkers, { passive: true });
    window.requestAnimationFrame(layoutMarkers);

    return {
      svg: inlineSvg,
      select(key) {
        activeKey = key || '';
        markerRecords.forEach((record) => {
          const isActive = record.country.key === activeKey;
          record.button.classList.toggle('is-active', isActive);
          record.button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        restoreSelectedTooltip();
      }
    };
  }

  function initGallery(root) {
    if (root.dataset.bvmInitialized === 'true') return;
    root.dataset.bvmInitialized = 'true';

    const grid = root.querySelector('[data-bvm-card-grid]');
    const cards = Array.from(root.querySelectorAll('[data-bvm-build-card]'));
    const countrySelect = root.querySelector('[data-bvm-country-select]');
    const productSelect = root.querySelector('[data-bvm-product-select]');
    const resultCount = root.querySelector('[data-bvm-result-count]');
    const emptyMessage = root.querySelector('[data-bvm-filter-empty]');
    const viewMore = root.querySelector('[data-bvm-view-more]');
    const activeBoxes = Array.from(root.querySelectorAll('[data-bvm-active-filters]'));
    const activeCopies = Array.from(root.querySelectorAll('[data-bvm-active-filter-copy]'));
    const clearButtons = Array.from(root.querySelectorAll('[data-bvm-clear-filters]'));
    const filterSummary = root.querySelector('[data-bvm-filter-summary]');
    const pageSize = Math.max(1, Number.parseInt(root.dataset.bvmPageSize || '12', 10));
    const coordinates = window.BVM_COUNTRY_COORDINATES || {};
    const lookup = buildCountryLookup(coordinates);
    const countries = new Map();
    const shippedCountryCodes = parseJsonArray(root, '[data-bvm-sales-countries]');
    const buildCountryRecords = parseBuildCountryRecords(root, '[data-bvm-build-countries]');
    const globalBuildCountryRecords = parseBuildCountryRecords(root, '[data-bvm-global-build-countries]');
    const strings = getGalleryStrings(root);

    let activeCountry = '';
    let activeCountryName = '';
    let activeProductId = '';
    let activeProductHandle = '';
    let activeProductName = '';
    let visibleCount = pageSize;
    let mapController = null;

    sortCustomerCards(cards, grid);
    sortSelectOptions(countrySelect);
    sortSelectOptions(productSelect);

    cards.forEach((card) => {
      const normalizedCountry = resolveCountryCode(card, coordinates, lookup);
      card.dataset.normalizedCountry = normalizedCountry;

      if (!normalizedCountry) return;

      const coordinate = coordinates[normalizedCountry];
      const iso2 = coordinate?.iso2 || normalizedCountry;
      const iso3 = coordinate?.iso3 || '';
      const name = card.dataset.countryName || coordinate?.name || iso2;
      const existing = countries.get(iso2) || {
        key: iso2,
        iso2,
        iso3,
        name,
        count: 0,
        xPercent: Number(coordinate?.xPercent),
        yPercent: Number(coordinate?.yPercent)
      };

      existing.count += 1;
      countries.set(iso2, existing);
      if (iso3) countries.set(iso3, existing);
    });

    // Server-rendered country records provide a second, independent source of truth.
    // This prevents a published country such as Morocco from being missed if a card
    // has an old or translated country value in its data attributes.
    buildCountryRecords.forEach((record) => {
      const normalizedCountry = resolveCountryCode({
        dataset: {
          countryCode: record.code,
          countryName: record.name,
          countryKey: record.key
        }
      }, coordinates, lookup);

      if (!normalizedCountry) return;

      const coordinate = coordinates[normalizedCountry];
      if (!coordinate) return;

      const iso2 = coordinate.iso2 || normalizedCountry;
      const iso3 = coordinate.iso3 || '';
      const existing = countries.get(iso2) || {
        key: iso2,
        iso2,
        iso3,
        name: record.name || coordinate.name || iso2,
        count: 0,
        xPercent: Number(coordinate.xPercent),
        yPercent: Number(coordinate.yPercent)
      };

      existing.count = Math.max(existing.count, record.count);
      countries.set(iso2, existing);
      if (iso3) countries.set(iso3, existing);
    });


    const globalPublishedCountryCodes = globalBuildCountryRecords
      .map((record) => resolveCountryCode({
        dataset: {
          countryCode: record.code,
          countryName: record.name,
          countryKey: record.key
        }
      }, coordinates, lookup))
      .map((code) => String(code || '').trim().toUpperCase())
      .filter((code) => /^[A-Z]{2,3}$/.test(code));

    const effectiveShippedCountryCodes = [...new Set([
      ...shippedCountryCodes,
      ...globalPublishedCountryCodes
    ])];

    function cardMatches(card) {
      const countryMatches = !activeCountry || card.dataset.normalizedCountry === activeCountry;
      if (!countryMatches) return false;

      if (!activeProductId && !activeProductHandle) return true;

      const ids = parseList(card.dataset.productIds);
      const handles = parseList(card.dataset.productHandles);

      if (activeProductId && ids.includes(activeProductId)) return true;
      return Boolean(activeProductHandle && handles.includes(activeProductHandle));
    }

    function updateMapState() {
      mapController?.select(activeCountry);
    }

    function updateFilterSummary() {
      const labels = [];
      if (activeCountryName) labels.push(activeCountryName);
      if (activeProductName) labels.push(activeProductName);

      if (filterSummary) {
        filterSummary.textContent = labels.length ? labels.join(' · ') : strings.allFilters;
      }

      const copy = labels.length ? labels.join(' · ') : '';
      activeCopies.forEach((element) => {
        element.textContent = copy;
      });
      activeBoxes.forEach((element) => {
        element.hidden = !labels.length;
      });
    }

    function updateSelectState() {
      if (countrySelect) countrySelect.value = activeCountry;
      if (productSelect) productSelect.value = activeProductId;
    }

    function updateViewMore(matchingCount) {
      if (!viewMore) return;

      const remaining = Math.max(0, matchingCount - visibleCount);
      viewMore.hidden = remaining === 0;
      if (remaining > 0) {
        const nextBatch = Math.min(pageSize, remaining);
        const label = nextBatch === 1
          ? strings.viewMoreOne
          : interpolate(strings.viewMoreOther, { count: nextBatch });
        viewMore.textContent = label;
        viewMore.setAttribute('aria-label', label);
      }
    }

    function render() {
      const matching = cards.filter(cardMatches);

      cards.forEach((card) => {
        card.hidden = true;
        card.setAttribute('aria-hidden', 'true');
      });

      matching.slice(0, visibleCount).forEach((card) => {
        card.hidden = false;
        card.setAttribute('aria-hidden', 'false');
      });

      if (resultCount) {
        resultCount.textContent = formatBuildCount(strings, matching.length);
      }

      if (emptyMessage) {
        emptyMessage.hidden = matching.length !== 0;
        if (matching.length === 0) {
          if (activeCountry && activeProductName) {
            emptyMessage.textContent = interpolate(strings.emptyCountryProduct, {
              country: activeCountryName,
              product: activeProductName
            });
          } else if (activeCountry) {
            emptyMessage.textContent = interpolate(strings.emptyCountry, {
              country: activeCountryName
            });
          } else {
            emptyMessage.textContent = strings.emptyProduct;
          }
        }
      }

      updateViewMore(matching.length);
      updateFilterSummary();
      updateSelectState();
      updateMapState();
    }

    function setCountry(key, name, scroll = true) {
      activeCountry = key || '';
      activeCountryName = name || '';
      visibleCount = pageSize;
      render();

      if (scroll) {
        root.querySelector('.bvm-cbg__gallery-section')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }

    function setProductFromSelect(scroll = false) {
      const option = productSelect?.selectedOptions?.[0];
      activeProductId = option?.value || '';
      activeProductHandle = option?.dataset.productHandle || '';
      activeProductName = option?.dataset.productName || '';
      visibleCount = pageSize;
      render();

      if (scroll) {
        root.querySelector('.bvm-cbg__gallery-section')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }

    countrySelect?.addEventListener('change', () => {
      const option = countrySelect.selectedOptions[0];
      setCountry(option?.value || '', option?.dataset.countryName || '', false);
    });

    productSelect?.addEventListener('change', () => {
      setProductFromSelect(false);
    });

    clearButtons.forEach((button) => {
      button.addEventListener('click', () => {
        activeCountry = '';
        activeCountryName = '';
        activeProductId = '';
        activeProductHandle = '';
        activeProductName = '';
        visibleCount = pageSize;
        render();
      });
    });

    viewMore?.addEventListener('click', () => {
      visibleCount += pageSize;
      render();
    });

    root.classList.add('is-enhanced');
    render();

    inlineMap(
      root,
      countries,
      effectiveShippedCountryCodes,
      (key, name) => setCountry(key, name, false),
      () => root.querySelector('.bvm-cbg__gallery-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      }),
      strings
    ).then((controller) => {
      mapController = controller;
      updateMapState();
    });
  }

  function initLightbox(root) {
    const lightbox = root.querySelector('[data-bvm-lightbox]');
    if (!lightbox || lightbox.dataset.bvmInitialized === 'true') return;
    lightbox.dataset.bvmInitialized = 'true';

    const image = lightbox.querySelector('[data-bvm-lightbox-image]');
    const title = lightbox.querySelector('[data-bvm-lightbox-title]');
    const caption = lightbox.querySelector('[data-bvm-lightbox-caption]');
    if (!image || !title || !caption) return;

    function close() {
      lightbox.hidden = true;
      image.removeAttribute('src');
      document.documentElement.style.removeProperty('overflow');
      lastLightboxTrigger?.focus();
    }

    function open(trigger) {
      const source = trigger.dataset.bvmLightboxSrc;
      if (!source) return;

      lastLightboxTrigger = trigger;
      image.src = source;
      image.alt = trigger.dataset.bvmLightboxAlt || '';
      title.textContent = trigger.dataset.bvmLightboxTitle || '';
      caption.textContent = trigger.dataset.bvmLightboxCaption || '';
      caption.hidden = !caption.textContent;
      lightbox.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      lightbox.querySelector('[data-bvm-lightbox-close]')?.focus();
    }

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-bvm-lightbox-open]');
      if (trigger) open(trigger);
    });

    lightbox.querySelectorAll('[data-bvm-lightbox-close]').forEach((button) => {
      button.addEventListener('click', close);
    });

    document.addEventListener('keydown', (event) => {
      if (!lightbox.hidden && event.key === 'Escape') close();
    });
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-bvm-cbg]').forEach((root) => {
      initGallery(root);
      initLightbox(root);
      root.querySelectorAll('[data-bvm-carousel]').forEach(initCarousel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
